import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { Check, Copy, Sparkles } from 'lucide-react';
import { Notice } from '@/ui/Panel';
import { ChatArtifactCard } from '@/routes/chat/ChatArtifact';
import type { ChatMessage } from '@/app/queries';

/**
 * One turn, written the way the two sides are actually read.
 *
 * A question is a short thing somebody said, so it is a bubble. An answer is
 * four paragraphs of reasoning about a squad, and four paragraphs in a bubble
 * is a wall — so the answer is set as a document: full measure, no border, the
 * assistant named above it. The two no longer look like the same kind of
 * object, which is what tells you at a glance whose words you are reading.
 */
export function ChatTurn({
  message,
  teamId,
  seasonId,
}: {
  message: ChatMessage;
  teamId: string | null;
  seasonId: string | null;
}) {
  const t = useTranslations();
  const [copied, setCopied] = useState(false);

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <p className="max-w-[46ch] whitespace-pre-wrap rounded-(--radius-panel) bg-ink px-4 py-2.5 leading-relaxed text-paper">
          {message.answer}
        </p>
      </div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.answer);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // A browser that refuses the clipboard is not a failure worth a banner;
      // the text is on screen and can be selected.
    }
  };

  return (
    <div className="group/turn max-w-[68ch]">
      <p className="mb-1.5 flex items-center gap-1.5 text-meta font-semibold text-ink-muted">
        <Sparkles aria-hidden="true" className="size-3.5" />
        {t('chat.assistant')}
      </p>

      <div className="whitespace-pre-wrap leading-relaxed">{message.answer}</div>

      {/* The reasoning travels with the answer, as everywhere else in this
          product — and quietly, because it explains the answer rather than
          competing with it. */}
      {message.why ? (
        <div className="mt-3 border-s-2 border-line ps-3">
          <p className="text-meta font-semibold text-ink-muted">{t('ai.why')}</p>
          <p className="text-meta leading-relaxed text-ink-muted">{message.why}</p>
        </div>
      ) : null}

      {/* The app's own words, identical every time and translated, rather than
          whatever the model chose to write. */}
      {message.touchesHealth ? (
        <div className="mt-3">
          <Notice tone="risk">{t('chat.healthDisclaimer')}</Notice>
        </div>
      ) : null}

      {message.artifacts.map((a, i) => (
        <ChatArtifactCard
          key={`${a.kind}-${i}`}
          artifact={a}
          teamId={teamId}
          seasonId={seasonId}
        />
      ))}

      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void copy()}
          className="-ms-2 inline-flex min-h-11 items-center gap-1.5 rounded-(--radius-control) px-2 text-meta text-ink-faint transition-colors hover:text-ink focus-visible:text-ink"
        >
          {copied ? (
            <Check aria-hidden="true" className="size-3.5 text-ok" />
          ) : (
            <Copy aria-hidden="true" className="size-3.5" />
          )}
          {copied ? t('chat.copied') : t('chat.copy')}
        </button>
        {message.credits > 0 ? (
          <span className="tabular text-meta text-ink-faint">
            {t('chat.costOf', { credits: message.credits })}
          </span>
        ) : null}
      </div>
    </div>
  );
}
